import { useEffect, useId, useRef } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { Button } from "./button";
import { Card, CardBody, CardFooter, CardHeader } from "./card";

export type ConfirmTone = "default" | "danger";

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description?: ComponentChildren;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  // True while the confirm handler is in flight — keeps the dialog open and
  // disables both buttons so a slow DELETE can't be canceled mid-request.
  busy?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "実行",
  cancelLabel = "キャンセル",
  tone = "default",
  busy = false,
  onClose,
  onConfirm,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // showModal() handles Esc + focus trap; mirror its close event into the
    // caller's onClose so external state stays in sync when the user hits Esc.
    const onCloseEvent = () => {
      if (!busy) onClose();
    };
    const onCancelEvent = (e: Event) => {
      if (busy) e.preventDefault();
    };
    el.addEventListener("close", onCloseEvent);
    el.addEventListener("cancel", onCancelEvent);
    return () => {
      el.removeEventListener("close", onCloseEvent);
      el.removeEventListener("cancel", onCancelEvent);
    };
  }, [onClose, busy]);

  function onBackdropClick(e: MouseEvent) {
    if (busy) return;
    if (e.target === ref.current) ref.current?.close();
  }

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      onClick={onBackdropClick}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      // Tailwind preflight zeroes the UA `margin: auto` that `showModal()`
      // relies on for centering, so restore it explicitly with `m-auto`.
      class="m-auto w-[min(95vw,28rem)] rounded-lg border-0 bg-transparent p-0 backdrop:bg-fg/40"
    >
      <Card elevated>
        <CardHeader>
          <h2 id={titleId} class="text-base font-semibold text-fg">
            {title}
          </h2>
        </CardHeader>
        {description && (
          <CardBody>
            <div id={descriptionId} class="text-sm text-fg-muted">
              {description}
            </div>
          </CardBody>
        )}
        <CardFooter class="flex flex-wrap justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "dangerSolid" : "primary"}
            size="sm"
            loading={busy}
            onClick={onConfirm}
          >
            {busy ? "実行中…" : confirmLabel}
          </Button>
        </CardFooter>
      </Card>
    </dialog>
  );
}
